import React, { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { client, setAuthToken } from "../api";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";

const MIN_LENGTH = 10;

/**
 * Redeems the one-time setup link issued when a store installs the app.
 *
 * Machine-provisioned accounts ship with a generated password the merchant
 * never chose. This is where they replace it; the token is single-use and
 * expires, so the link cannot be reused from an old inbox.
 */
const SetPasswordPage = ({ onLogin }) => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < MIN_LENGTH) {
      setError(`Password must be at least ${MIN_LENGTH} characters.`);
      return;
    }
    if (password !== confirm) {
      setError("The two passwords do not match.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await client.post("/api/auth/complete-setup", { token, password });
      setAuthToken(data.access_token);
      onLogin(data);
      navigate("/admin/dashboard/overview", { replace: true });
    } catch (err) {
      const d = err?.response?.data?.detail;
      setError(
        typeof d === "string"
          ? d
          : Array.isArray(d)
            ? d.map((x) => x?.msg).filter(Boolean).join("; ")
            : "Could not set the password.",
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ display: "flex", minHeight: "100vh", alignItems: "center", justifyContent: "center", p: 2 }}>
      <Card sx={{ width: "100%", maxWidth: 460, borderRadius: 3 }} elevation={3}>
        <CardContent sx={{ p: { xs: 3, sm: 4 } }}>
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 3 }}>
            <Box
              sx={{
                width: 42, height: 42, borderRadius: 2, display: "grid", placeItems: "center",
                bgcolor: "primary.main", color: "primary.contrastText",
              }}
            >
              <AutoAwesomeRoundedIcon fontSize="small" />
            </Box>
            <Typography variant="h6" fontWeight={700}>
              Product Assistant Studio
            </Typography>
          </Stack>

          <Typography variant="h5" fontWeight={700} gutterBottom>
            Choose your password
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Your store was connected automatically. Set a password to take
            ownership of the account.
          </Typography>

          {!token && (
            <Alert severity="error" sx={{ mb: 2 }}>
              This link is missing its setup token. Ask for a new one from the
              app listing in your Shopify admin.
            </Alert>
          )}
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <form onSubmit={submit}>
            <Stack spacing={2}>
              <TextField
                label="New password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                fullWidth
                autoFocus
                disabled={!token || loading}
                helperText={`At least ${MIN_LENGTH} characters.`}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockOutlinedIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
              <TextField
                label="Confirm password"
                type="password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                fullWidth
                disabled={!token || loading}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockOutlinedIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
              <Button
                type="submit"
                variant="contained"
                size="large"
                disabled={!token || loading}
                fullWidth
              >
                {loading ? "Saving…" : "Set password and sign in"}
              </Button>
            </Stack>
          </form>
        </CardContent>
      </Card>
    </Box>
  );
};

export default SetPasswordPage;
